import { TFunction } from 'i18next';
import moment from 'moment';
import React, { ForwardedRef, useState } from 'react';
import { Dropdown, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';
import {
  BiAlarmSnooze,
  BiBarChartAlt2,
  BiBook,
  BiConversation,
  BiDotsVerticalRounded,
  BiPencil,
  BiPlay,
  BiSolidDuplicate,
  BiSolidMagicWand,
  BiSolidMessageDetail,
  BiSolidPalette,
  BiTrash,
} from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import {
  ChatbotRTKService,
  useDeactivateAiTypeMutation,
  useDeactivateMcBotTypeMutation,
  useDeleteScenarioMutation,
  useGetAssistantDetailMutation,
  useKnowledgeBaseDuplicateMutation,
  useSetDefaultScenariosMutation,
} from '@/app/services/chatbot-rtk.services';
import { IGetAllScenariosResponse } from '@/app/services/chatbot-rtk.type';
import { confirmWithDialog } from '@/components/common/confirmation/confirm-dialog';
import { UsageBot } from '@/components/common/Usage';
import { BotDependency } from '@/components/dashboard/BotDependency';
import { PSButton } from '@/components/ui/button';
import { AlertsType } from '@/constants/alerts.constant';
import { MultiLanguageUrl } from '@/constants/multiLanguage.constant';
import { BiLouise } from '@/features/appearance/AppearanceSharedAi';
import {
  botsUiState,
  handleInitializeEditWizard,
  handleSetLivePreviewTargetBot,
  handleToggleBotsIntegrationModal,
  handleToggleCloneShareModal,
} from '@/features/bot/botSlice';
import { AIKittyType, BotCode, McBotFaceType } from '@/features/bot/types';
import { setCurrentScenario, setCurrentScenarioTitle } from '@/features/scenario.slice';
import Utilities from '@/utils';
import { BotCategoryEnum } from '@/utils/BotCategoryEnum';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import usePermission from '../../../hooks/usePermission';

const renderStatusBadge = (status: string, t: TFunction<'translation', undefined>) => {
  const types: Record<string, React.ReactNode> = {
    waiting_scan: (
      <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-sky-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
        <div className="tw-text-center tw-text-sky-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key4')}</div>
      </div>
    ),
    ready: (
      <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-emerald-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
        <div className="tw-text-center tw-text-emerald-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key5')}</div>
      </div>
    ),
    failed: (
      <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-red-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key6')}</div>
      </div>
    ),
    updating_bot: (
      <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-amber-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
        <div className="tw-text-center tw-text-amber-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key7')}</div>
      </div>
    ),
    scan_in_progress: (
      <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-sky-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
        <div className="tw-text-center tw-text-sky-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key9')}</div>
      </div>
    ),
    disabled: (
      <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-red-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key13')}</div>
      </div>
    ),
    not_published: (
      <div className="tw-flex tw-flex-row tw-gap-2 tw-items-center">
        <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-gray-400 tw-rounded-[10px] tw-flex tw-justify-center tw-items-center">
          <div className="tw-text-center tw-text-white tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key15')}</div>
        </div>
        <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-emerald-100 tw-rounded-[10px] tw-flex tw-justify-center tw-items-center">
          <div className="tw-text-center tw-text-emerald-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key5')}</div>
        </div>
      </div>
    ),
    migrate_failed: (
      <div className="tw-w-max tw-h-4 tw-px-2 tw-py-0.5 tw-bg-red-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex">
        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">{t('General/key17')}</div>
      </div>
    ),
  };

  return types[status];
};

const CustomToggle = React.forwardRef<HTMLSpanElement, { children: React.ReactNode; onClick: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void }>(
  ({ children, onClick }, ref) => (
    <span
      ref={ref as ForwardedRef<HTMLSpanElement>}
      role="button"
      tabIndex={0}
      onClick={e => {
        e.preventDefault();
        onClick(e);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent<HTMLSpanElement, MouseEvent>);
        }
      }}
    >
      {children}
      <BiDotsVerticalRounded className="tw-cursor-pointer tw-w-5 tw-h-5" />
    </span>
  )
);
CustomToggle.displayName = 'CustomToggle';

const ChatBotItem = ({
  data,
  source,
  type,
  onSourceMutation,
}: {
  data: IGetAllScenariosResponse;
  source?: IGetAllScenariosResponse[];
  type: 'widget' | 'link';
  onSourceMutation: React.Dispatch<React.SetStateAction<IGetAllScenariosResponse[]>>;
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const permissions = usePermission();
  const { livePreviewTargetBot } = useAppSelector(botsUiState);
  const [loading, setLoading] = useState(false);

  const shouldHideDisplayedName = [BotTypeEnum.VIDEO_SCENARIO.toString(), BotTypeEnum.CALLBOT.toString()].includes(data.type);

  const [actionType, setActionType] = useState<'edit' | 'appearance' | 'visibility'>();

  const [deleteScenarioMutation, deleteScenarioMutationStatus] = useDeleteScenarioMutation();

  const [getAssistantDetails, getAssistantDetailsStatus] = useGetAssistantDetailMutation();

  const [setDefaultScenarios, setDefaultScenariosStatus] = useSetDefaultScenariosMutation();

  const [deactivateAiType, deactivateAiTypeResults] = useDeactivateAiTypeMutation();

  const [deactivateMcBotType, deactivateMcBotTypeResults] = useDeactivateMcBotTypeMutation();

  const [knowledgeBaseDuplicateRequest] = useKnowledgeBaseDuplicateMutation();

  const renderVideoFloatingButton = (icon: { type: string; value: string }) => {
    const icons: Record<string, React.ReactNode> = {
      '4': <BiLouise />,
      '1': <BiSolidDuplicate size={30} />,
      '2': <BiSolidMessageDetail size={30} />,
      '3': <BiSolidPalette size={30} />,
    };

    if (icon.type === 'icon') {
      return (
        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-blue-500 tw-text-white">
          {icons[icon.value]}
        </div>
      );
    } else if (icon.type === 'image') {
      return <img src={icon.value} alt={''} className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover" />;
    } else if (icon.type === 'video') {
      return (
        <video autoPlay loop muted playsInline className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover">
          <source src={icon.value}></source>
        </video>
      );
    } else {
      return null;
    }
  };

  const handleRemoveChatbot = (data: IGetAllScenariosResponse) => {
    const title = `${t(AlertsType.ISKBKO001)} "${data.title}"?`;
    let description = '';
    if ((data?.dependencies?.requiredFor?.length ?? 0) > 0) {
      const list = source?.filter(q => data?.dependencies?.requiredFor?.includes(q.id));
      const requiredTitles = list?.map((item: any) => item.title).join(',');
      if (list && list.length > 0) {
        description = t('DeleteModal/key7') + requiredTitles;
      }
    }
    confirmWithDialog(title, {
      confirmButtonText: t('GlobalConfirmationModal/confirm'),
      cancelButtonText: t('GlobalConfirmationModal/cancel'),
      confirmButtonStyle: 'danger',
      cancelButtonStyle: 'secondary',
      description: description,
    }).then(res => {
      if (res) {
        deleteScenarioMutation(data.id)
          .unwrap()
          .then(() => {
            onSourceMutation(prev => prev.filter(item => item.id !== data.id));

            dispatch(ChatbotRTKService.util.invalidateTags(['GetAllScenarios', 'Package']));
          })
          .catch(err => {
            toast.error(err.data.message || err.message);
          });
      }
    });
  };

  const handleVisibilityTimes = (data: IGetAllScenariosResponse) => {
    dispatch(setCurrentScenarioTitle(data.title));
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${data.id}&tab=operating-hours`);
  };

  const handleDuplicateBot = (data: IGetAllScenariosResponse) => {
    confirmWithDialog(t('Wizard/EditOptions/DuplicateConfirm', { botTitle: data.title }), {
      confirmButtonText: t('GlobalConfirmationModal/confirm'),
      cancelButtonText: t('GlobalConfirmationModal/cancel'),
      confirmButtonStyle: 'warning',
      cancelButtonStyle: 'secondary',
    }).then(res => {
      if (res) {
        knowledgeBaseDuplicateRequest(data.id)
          .then(res => {
            t('Wizard/EditOptions/DuplicateSuccessMsg');
          })
          .catch(err => {
            toast.error(err.data.message || err.message);
          });
      }
    });
  };

  const handleEditChatbot = (data: IGetAllScenariosResponse) => {
    dispatch(setCurrentScenarioTitle(data.title));
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${data.id}&tab=config`);
  };

  const handleEditScenarios = (data: IGetAllScenariosResponse) => {
    getAssistantDetails(data.id)
      .unwrap()
      .then(value => {
        dispatch(
          handleInitializeEditWizard({
            title: value.title,
            showModal: false,
            id: value.id,
            bot_code: value.type as BotCode,
            status: data.status,
            bot_type:
              (value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO || (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
                : BotCategoryEnum.AI_BASED_CHATBOT,
            bot_config:
              (value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO || (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                ? ({
                    bot_code: value.type,
                    title: data.title,
                    template_id: 1,
                  } as McBotFaceType)
                : ({ ...value.config } as AIKittyType),
          })
        );

        dispatch(setCurrentScenarioTitle(value.title));
        dispatch(setCurrentScenario(data.id));

        return navigate(`editor?type=${data.type === BotTypeEnum.TEXT_SCENARIO ? 'text-scenario' : 'video-scenario'}`);
      })
      .catch(err => {
        toast.error(err.data.message || err.message);
      });
  };

  const handleChatbotActivation = (data: IGetAllScenariosResponse) => {
    if (data.is_default) {
      if (data.type === BotTypeEnum.VIDEO_SCENARIO || data.type === BotTypeEnum.TEXT_SCENARIO) {
        deactivateMcBotType(data.id)
          .unwrap()
          .then(() => {
            toast.success(t('BillingPage/btn-05'));
          })
          .catch(err => {
            toast.error(err.data.message || err.message);
          });
      } else {
        deactivateAiType(data.id)
          .unwrap()
          .then(() => {
            toast.success(t('BillingPage/btn-05'));
          })
          .catch(err => {
            toast.error(err.data.message || err.message);
          });
      }
    } else {
      setDefaultScenarios(data.id)
        .unwrap()
        .then(() => {
          toast.success(t('BillingPage/btn-04'));
        })
        .catch(err => {
          if (err?.status == 401) {
            toast.warning(err.data.message || err.message);
          } else if (err?.data?.code == 'ISSYKO008') {
            toast.error(t(AlertsType.ISSYKO008));
          } else {
            toast.error(err.data.message || err.message);
          }
        });
    }
  };

  const handleIntegrateChatbot = (data: IGetAllScenariosResponse) => {
    dispatch(
      type === 'widget'
        ? handleToggleBotsIntegrationModal({
            status: true,
            extra: {
              botId: data.id,
              botCode: data.type as BotCode,
              title: data.title,
            },
          })
        : handleToggleCloneShareModal({
            status: true,
            data: { cloneId: data.id, logo: data.logo },
          })
    );
  };

  const handleEditAppearance = (data: IGetAllScenariosResponse) => {
    dispatch(setCurrentScenarioTitle(data.title));
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${data.id}&tab=appearance`);
  };

  const handleConversationHistory = (data: IGetAllScenariosResponse) => {
    dispatch(setCurrentScenarioTitle(data.title));
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${data.id}&tab=history-message`);
  };

  const handleEditStatistic = (data: IGetAllScenariosResponse) => {
    dispatch(setCurrentScenarioTitle(data.title));
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${data.id}&tab=statistics`);
  };

  const handleEditKnowledgeBase = (data: IGetAllScenariosResponse) => {
    dispatch(setCurrentScenarioTitle(data.title));
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${data.id}&tab=knowledgeBases`);
  };

  const handleManageLivePreview = (id: string) => {
    setLoading(true);
    if (livePreviewTargetBot) {
      dispatch(handleSetLivePreviewTargetBot(''));

      setTimeout(() => {
        dispatch(handleSetLivePreviewTargetBot(id));
      }, 2000);
    } else {
      dispatch(handleSetLivePreviewTargetBot(id));
    }
    setTimeout(() => {
      setLoading(false);
    }, 5000);
  };

  const usageStats = (data: IGetAllScenariosResponse) => {
    if (permissions.settingsPage.widget.customization.sessionLimit && data.sessionLimit !== -1) {
      return <UsageBot use={data.sessionUsage} total={data.sessionLimit as number} />;
    } else {
      return (
        <div className="tw-w-max  tw-px-4 tw-py-0.5 tw-bg-gray-400 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-h-[1rem] ">
          <div className="tw-text-center tw-text-white tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none tw-inline-flex">
            {data.sessionUsage} <BiSolidMessageDetail />
          </div>
        </div>
      );
    }
  };

  return (
    <div key={data.id} className="tw-self-stretch tw-py-2 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-flex tw-flex-col tw-w-full">
      {/* Main Row - Grid Layout */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[1fr_auto] tw-gap-3 tw-items-center tw-w-full tw-px-4 md:tw-px-6 tw-py-3">
        {/* Bot Info Column */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-flex-shrink-0">
            <div className="tw-relative">
              {data.type === BotTypeEnum.VIDEO_SCENARIO ? (
                renderVideoFloatingButton(data.icon)
              ) : (
                <img
                  className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                  src={data.logo && data.logo != '' ? data.logo : 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png'}
                  alt="chatbot logo"
                />
              )}

              <OverlayTrigger
                overlay={
                  <Tooltip>
                    {t('General/key3')} {!data.is_default ? t('DashboardHeaderSection/deactivated') : t('DashboardHeaderSection/activated')}
                  </Tooltip>
                }
              >
                <div
                  className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                    !data.is_default ? 'tw-bg-red-400' : 'tw-bg-emerald-400 blink'
                  } tw-rounded-full tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                />
              </OverlayTrigger>
            </div>
            {!shouldHideDisplayedName && data.displayedName && (
              <div className="tw-text-gray-500 tw-text-[10px] tw-font-normal tw-font-['Lexend'] tw-leading-none tw-truncate tw-max-w-[80px] tw-text-center">
                {data.displayedName}
              </div>
            )}
          </div>
          <div className="tw-flex-col tw-justify-start tw-items-start tw-inline-flex tw-min-w-0">
            <div className="tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <span>{data?.title}</span>
              {data?.packageName != null && data?.packageName != '' && (
                <span className="tw-px-2 tw-py-0.5 tw-bg-gray-100 tw-rounded-[10px] tw-text-gray-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-whitespace-nowrap">
                  {data?.packageName}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions Column */}
        <div className="tw-flex tw-items-center tw-gap-2 md:tw-gap-4 tw-flex-wrap md:tw-flex-nowrap tw-justify-end">
          {/* Preview Button */}
          {[
            BotTypeEnum.TEXT_SCENARIO,
            BotTypeEnum.AI_READY_TO_USE,
            BotTypeEnum.AI_WITH_KNOWLEDGE_BASE,
            BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY,
            BotTypeEnum.VIDEO_SCENARIO,
            BotTypeEnum.CALLBOT,
          ].includes(data.type as BotTypeEnum) &&
            (data.status === 'ready' || data.status === 'not_published') && (
              <PSButton
                id="btn-knowledge-preview"
                variant="outline"
                className="tw-gap-2 tw-group"
                aria-label={t('KnowledgeBasesCreateAssistantBot/btn-05')}
                onClick={() => {
                  handleManageLivePreview(data.id);
                }}
                disabled={data.type === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY && data.status !== 'ready'}
              >
                {loading ? <Spinner size="sm" /> : <BiPlay className="tw-w-4 tw-h-4 tw-relative tw-text-blue-600 group-hover:tw-text-white" />}
                <span className="tw-hidden sm:tw-inline">{t('KnowledgeBasesCreateAssistantBot/btn-05')}</span>
              </PSButton>
            )}

          {/* Action Buttons */}
          <div className="tw-flex tw-items-center tw-gap-4">
            <PSButton
              id="btn-knowledge-integrate"
              variant="primary"
              className="tw-border-0 tw-bg-blue-700 tw-rounded-[17px] tw-shadow tw-justify-center tw-items-center tw-flex tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              onClick={() => handleIntegrateChatbot(data)}
              title={data.type === BotTypeEnum.HUMAN_CLONE ? t('LatestChatBotsQuickAccess/key7') : t('Wizard/general/key8')}
            />

            {deleteScenarioMutationStatus.isLoading && <Spinner size="sm" className="tw-mr-2" />}

            {!deleteScenarioMutationStatus?.isLoading && (
              <Dropdown>
                <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setActionType('edit');
                      handleEditChatbot(data);
                    }}
                    eventKey="4"
                  >
                    <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                      <BiPencil className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t('Wizard/EditOptions/EditBotProfile')}
                      </div>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Item
                    eventKey="3"
                    onClick={() => {
                      setActionType('appearance');
                      handleEditAppearance(data);
                    }}
                  >
                    <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                      <BiSolidPalette className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Wizard/EditOptions/Appearance')}</div>
                    </div>
                  </Dropdown.Item>

                  {[BotTypeEnum.TEXT_SCENARIO, BotTypeEnum.VIDEO_SCENARIO].includes(data.type as BotTypeEnum) && (
                    <Dropdown.Item
                      disabled={getAssistantDetailsStatus.isLoading}
                      onClick={() => {
                        setActionType('edit');
                        handleEditScenarios(data);
                      }}
                    >
                      <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                        <BiSolidMagicWand className="tw-w-5 tw-h-5 tw-relative" />
                        <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {t('Wizard/EditOptions/EditScenario')}
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}

                  {(data.type === BotTypeEnum.HUMAN_CLONE || data.type === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE) && (
                    <Dropdown.Item
                      onClick={() => {
                        setActionType('edit');
                        handleEditKnowledgeBase(data);
                      }}
                      eventKey="4"
                    >
                      <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                        <BiBook className="tw-w-5 tw-h-5 tw-relative" />
                        <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {t('Wizard/EditOptions/KnowledgeBase')}
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}

                  {(data.type === 'KITTY_01' ||
                    data.type === BotTypeEnum.AI_READY_TO_USE ||
                    data.type === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE ||
                    data.type === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY ||
                    data.type === BotTypeEnum.CALLBOT) && (
                    <Dropdown.Item
                      onClick={() => {
                        handleConversationHistory(data);
                      }}
                      eventKey="1"
                    >
                      <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                        <BiConversation className="tw-w-5 tw-h-5 tw-relative" />
                        <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {t('Wizard/EditOptions/ConversationHistory')}
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}

                  {data.type != BotTypeEnum.HUMAN_CLONE && (
                    <Dropdown.Item
                      onClick={() => {
                        handleEditStatistic(data);
                      }}
                      eventKey="1"
                    >
                      <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                        <BiBarChartAlt2 className="tw-w-5 tw-h-5 tw-relative" />
                        <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {t('Wizard/EditOptions/Statistics')}
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}

                  <Dropdown.Item
                    onClick={() => {
                      setActionType('visibility');
                      handleVisibilityTimes(data);
                    }}
                    eventKey="2"
                  >
                    <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                      <BiAlarmSnooze className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t('Wizard/EditOptions/OperatingHours')}
                      </div>
                    </div>
                  </Dropdown.Item>

                  {[BotTypeEnum.TEXT_SCENARIO, BotTypeEnum.VIDEO_SCENARIO].includes(data.type as BotTypeEnum) && (
                    <Dropdown.Item
                      onClick={() => {
                        handleDuplicateBot(data);
                      }}
                    >
                      <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                        <BiSolidDuplicate className="tw-w-5 tw-h-5 tw-relative" />
                        <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {t('Wizard/EditOptions/Duplicate')}
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}

                  <Dropdown.Item onClick={() => handleRemoveChatbot(data)} disabled={deleteScenarioMutationStatus.isLoading} eventKey="5">
                    <div className="tw-h-9 tw-justify-start tw-items-center tw-gap-3 tw-inline-flex">
                      <BiTrash className="tw-w-5 tw-h-5 tw-relative tw-text-red-500" />
                      <div className="tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-text-red-500">
                        {t('Wizard/EditOptions/DeleteChatbot')}
                      </div>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>
      </div>

      {/* Pending Transaction Message for Video Bots */}
      {data.type === BotTypeEnum.VIDEO_SCENARIO && data.buyer_transaction_status === 'PENDING' && data.transactionId && (
        <div className="tw-mx-4 md:tw-mx-6 tw-my-2 tw-p-3 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg">
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-items-start sm:tw-items-center">
            <div className="tw-flex-1">
              <p className="tw-text-amber-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-relaxed tw-m-0">
                {ReactHtmlParser(t('KnowledgeBasesPage/pendingTransaction/message'))}
              </p>
            </div>
            <button
              onClick={() => handleEditScenarios(data)}
              disabled={getAssistantDetailsStatus.isLoading}
              className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-amber-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium tw-font-['Lexend'] hover:tw-bg-amber-700 tw-transition tw-whitespace-nowrap tw-border-0 tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              <BiSolidMagicWand className="tw-w-4 tw-h-4" />
              {t('Wizard/EditOptions/EditScenario')}
            </button>
          </div>
        </div>
      )}

      {/* Bottom Row - Metadata Flex */}
      <div className="tw-px-4 md:tw-px-6 tw-py-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-bg-gray-50 tw-border-t tw-border-gray-100">
        {/* Type */}
        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          <span className="tw-text-gray-600 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{Utilities.getBotNameByBotCode(data.type, t)}</span>
        </div>

        <span className="tw-text-gray-300 tw-hidden sm:tw-inline">|</span>

        {/* Date */}
        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          <span className="tw-text-gray-400 tw-text-[10px] tw-font-medium tw-uppercase">{t('General/created')}:</span>
          <span className="tw-text-gray-600 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{moment(data.creation_date_time).format('ll')}</span>
        </div>

        <span className="tw-text-gray-300 tw-hidden sm:tw-inline">|</span>

        {/* Status Badge */}
        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          <span className="tw-text-gray-400 tw-text-[10px] tw-font-medium tw-uppercase">{t('General/status')}:</span>
          {renderStatusBadge(data.status, t)}
        </div>

        <span className="tw-text-gray-300 tw-hidden sm:tw:inline">|</span>

        {/* Usage Stats */}
        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          <span className="tw-text-gray-400 tw-text-[10px] tw-font-medium tw-uppercase">{t('General/usage')}:</span>
          {usageStats(data)}
        </div>

        {/* Feature Indicators */}
        {(data?.saveStats || data?.saveConversation || data?.operatingHours) && (
          <>
            <span className="tw-text-gray-300 tw:hidden sm:tw:inline">|</span>
            <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
              <span className="tw-text-gray-400 tw-text-[10px] tw-font-medium tw-uppercase">{t('General/features')}:</span>
              <div className="tw-flex tw-gap-2 tw-items-center tw-text-gray-500">
                {data?.saveStats && <BiBarChartAlt2 className="blink" title={t('Wizard/EditOptions/Statistics')} />}
                {data?.saveConversation && <BiConversation className="blink" title={t('Wizard/EditOptions/ConversationHistory')} />}
                {data?.operatingHours && <BiAlarmSnooze title={t('Wizard/EditOptions/OperatingHours')} />}
              </div>
            </div>
          </>
        )}

        {/* Dependencies */}
        {((data?.dependencies?.requiredFor?.length ?? 0) > 0 || (data?.dependencies?.dependsOn?.length ?? 0) > 0) && (
          <>
            <span className="tw-text-gray-300 tw:hidden sm:tw:inline">|</span>
            <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
              <span className="tw-text-gray-400 tw-text-[10px] tw-font-medium tw-uppercase">{t('General/dependencies')}:</span>
              <BotDependency item={data} source={source} />
            </div>
          </>
        )}

        {/* Activation Switch */}
        {data.status !== 'disabled' && (
          <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0 tw-ml-auto">
            <span className="tw-text-gray-400 tw-text-[10px] tw-font-medium tw-uppercase">{t('General/active')}:</span>
            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              {!setDefaultScenariosStatus.isLoading && !deactivateAiTypeResults.isLoading && !deactivateMcBotTypeResults.isLoading && (
                <Form.Check
                  checked={data.is_default}
                  disabled={
                    deactivateAiTypeResults.isLoading ||
                    deactivateMcBotTypeResults.isLoading ||
                    (data.type === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY && data.status !== 'ready')
                  }
                  type="switch"
                  onChange={() => {
                    handleChatbotActivation(data);
                  }}
                  className="form-switch-small tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                />
              )}
              {(setDefaultScenariosStatus.isLoading || deactivateAiTypeResults.isLoading || deactivateMcBotTypeResults.isLoading) && (
                <Spinner size="sm" className="tw-mr-2" />
              )}
            </Form.Group>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBotItem;
